import React, { useMemo } from "react";
import HelpLink from "../help/HelpLink";
import { estimateFtp, recentRides } from "../../utils/progress";

const day = (iso) =>
  new Date(iso).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });

/**
 * FTP from your best 20 minutes on the bike (#203), and what it gives each
 * ride: Intensity Factor (how hard against FTP) and Training Stress Score
 * (an hour at FTP is 100). Hidden until there's a 20 minute effort.
 */
const FtpCard = ({ rows, weightKg }) => {
  const ftp = useMemo(() => estimateFtp(rows), [rows]);
  const rides = useMemo(() => (ftp ? recentRides(rows, ftp.watts) : []), [rows, ftp]);
  if (!ftp) return null;
  const perKg = weightKg > 0 ? Math.round((ftp.watts / weightKg) * 10) / 10 : null;
  return (
    <section aria-label="FTP" className="grid gap-3 rounded-2xl border border-border bg-card p-3.5">
      <h3 className="flex items-center gap-1 text-[15px] font-bold text-white">
        FTP
        <HelpLink topic="progress#ftp" label="About FTP" />
      </h3>
      <dl className="grid grid-cols-3 gap-2">
        <div className="grid gap-0.5">
          <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">Bike FTP</dt>
          <dd className="text-lg font-semibold tabular-nums">{ftp.watts} W</dd>
          <dd className="text-[11px] text-muted-foreground">95% of best 20 min</dd>
        </div>
        {perKg && (
          <div className="grid gap-0.5">
            <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">Per kg</dt>
            <dd className="text-lg font-semibold tabular-nums">{perKg}</dd>
            <dd className="text-[11px] text-muted-foreground">W/kg at {weightKg} kg</dd>
          </div>
        )}
        {ftp.change != null && (
          <div className="grid gap-0.5">
            <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">Change</dt>
            <dd className="text-lg font-semibold tabular-nums">
              {ftp.change > 0 ? "+" : ftp.change < 0 ? "−" : "±"}
              {Math.abs(ftp.change)} W
            </dd>
            <dd className="text-[11px] text-muted-foreground">in 30 days</dd>
          </div>
        )}
      </dl>
      {rides.length > 0 && (
        <ul className="grid gap-1 border-t border-border pt-2 text-xs tabular-nums">
          {rides.map(({ row, stress }) => (
            <li key={row.id} className="flex justify-between gap-3">
              <span className="min-w-0 truncate text-muted-foreground">
                {day(row.started_at)} · {row.workout_type || "Ride"}
              </span>
              <span className="shrink-0 font-semibold text-foreground">
                IF {stress.if.toFixed(2)} · TSS {stress.tss}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default FtpCard;
